import React from 'react';
import { useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import SiteNav from './marketing/SiteNav';
import SiteFooter from './marketing/SiteFooter';
import { Button } from './ui/button';
import { SERVICE_PILLARS } from '../mock';

const Icon = ({ name, className }) => { const C = Icons[name] || Icons.Circle; return <C className={className} />; };

const AUDIENCES = [
  { icon: 'Building2', title: 'Property Managers', desc: 'Book inventories, safety certificates and inspections, then track every job to completion in one dashboard.', to: '/property-managers' },
  { icon: 'Wrench', title: 'Contractors', desc: 'Receive assigned jobs, complete inspections on-site and upload reports and summaries.', to: '/contractors' },
  { icon: 'Network', title: 'Franchise Operators', desc: 'Run multiple branches and teams under one platform with central reporting and compliance.', to: '/franchise' },
];

const STEPS = [
  { icon: 'MousePointerClick', title: 'Choose a service', desc: 'Pick from inventories, safety certificates, maintenance or specialist services.' },
  { icon: 'CalendarCheck', title: 'Book & schedule', desc: 'Enter the property, size and date — fixed price or request a quote.' },
  { icon: 'UserCheck', title: 'Contractor assigned', desc: 'We assign an accredited contractor and you track progress live.' },
  { icon: 'FileCheck2', title: 'Get your report', desc: 'Download compliant PDF reports, invoices and compliance docs.' },
];

const Home = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background">
      <SiteNav />

      {/* Hero */}
      <section className="hero-gradient pt-[68px]">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-white fade-up">
            <span className="inline-flex items-center gap-2 text-[#7fb0f5] text-sm font-semibold tracking-wide uppercase mb-5"><Icons.ShieldCheck className="h-4 w-4" /> Independent & compliant reporting</span>
            <h1 className="font-display font-extrabold text-4xl md:text-5xl leading-tight">All your property services, booked and tracked in one place.</h1>
            <p className="mt-6 text-lg text-slate-300">Check-a-Property helps landlords, agents and contractors order inventories, safety certificates and inspections — without juggling inboxes, spreadsheets and suppliers.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button size="lg" onClick={() => navigate('/book')} className="bg-white text-[#102d55] hover:bg-slate-100 font-semibold px-7">Book a service <Icons.ArrowRight className="ml-2 h-4 w-4" /></Button>
              <Button size="lg" variant="outline" onClick={() => navigate('/services')} className="bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white px-7">View services</Button>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-300">
              {['No subscription to get started', 'Accredited contractors', 'Dispute-ready reports'].map((t) => (
                <span key={t} className="flex items-center gap-2"><Icons.Check className="h-4 w-4 text-[#7fb0f5]" /> {t}</span>
              ))}
            </div>
          </div>
          <div className="bg-white/95 rounded-2xl shadow-2xl p-6 lg:p-8">
            <div className="text-sm font-semibold text-slate-500 mb-4">Popular services</div>
            <div className="space-y-3">
              {SERVICE_PILLARS.map((p) => (
                <button key={p.id} onClick={() => navigate('/services')} className="w-full flex items-center gap-4 p-3.5 rounded-xl border border-border hover:border-[#1c63c4] hover:bg-secondary/60 transition-colors text-left">
                  <span className="h-11 w-11 rounded-lg bg-[#102d55] text-white flex items-center justify-center shrink-0"><Icon name={p.icon} className="h-5 w-5" /></span>
                  <span className="flex-1"><span className="block font-semibold text-[#102d55]">{p.name}</span><span className="block text-xs text-muted-foreground">{p.options.length} options</span></span>
                  <Icons.ChevronRight className="h-5 w-5 text-slate-400" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Service pillars */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-14">
          <h2 className="font-display font-extrabold text-3xl md:text-4xl text-[#102d55]">Four service pillars</h2>
          <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">Everything you need to keep a property compliant, safe and well-maintained — from one trusted supplier.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {SERVICE_PILLARS.map((p) => (
            <div key={p.id} className="bg-white rounded-2xl border border-border p-6 card-hover">
              <div className="h-12 w-12 rounded-xl bg-[#eaf1fb] text-[#1c63c4] flex items-center justify-center mb-4"><Icon name={p.icon} className="h-6 w-6" /></div>
              <h3 className="font-display font-bold text-lg text-[#102d55]">{p.name}</h3>
              <p className="text-sm text-muted-foreground mt-2">{p.blurb}</p>
              <ul className="mt-4 space-y-1.5">
                {p.options.slice(0, 3).map((o) => <li key={o.id} className="text-sm text-slate-600 flex items-center gap-2"><Icons.Dot className="h-4 w-4 text-[#1c63c4]" /> {o.name}</li>)}
              </ul>
              <button onClick={() => navigate('/services')} className="mt-5 text-sm font-semibold text-[#1c63c4] flex items-center gap-1">View options <Icons.ArrowRight className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-secondary/50 py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14"><h2 className="font-display font-extrabold text-3xl text-[#102d55]">How it works</h2></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <div key={s.title} className="bg-white rounded-2xl border border-border p-6 relative">
                <span className="absolute top-5 right-5 text-3xl font-display font-extrabold text-slate-100">{i + 1}</span>
                <div className="h-11 w-11 rounded-xl bg-[#102d55] text-white flex items-center justify-center mb-4"><Icon name={s.icon} className="h-5 w-5" /></div>
                <h3 className="font-display font-semibold text-[#102d55]">{s.title}</h3>
                <p className="text-sm text-muted-foreground mt-2">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Audiences */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid md:grid-cols-3 gap-6">
          {AUDIENCES.map((a) => (
            <div key={a.title} className="bg-white rounded-2xl border border-border p-8 card-hover">
              <div className="h-12 w-12 rounded-xl bg-[#eaf1fb] text-[#1c63c4] flex items-center justify-center mb-4"><Icon name={a.icon} className="h-6 w-6" /></div>
              <h3 className="font-display font-bold text-xl text-[#102d55]">{a.title}</h3>
              <p className="mt-3 text-muted-foreground">{a.desc}</p>
              <button onClick={() => navigate(a.to)} className="mt-5 text-sm font-semibold text-[#1c63c4] flex items-center gap-1">Learn more <Icons.ArrowRight className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="hero-gradient py-20 px-6 text-center text-white">
        <h2 className="font-display font-extrabold text-3xl md:text-4xl">Ready to book your first inspection?</h2>
        <p className="mt-4 text-slate-300 max-w-xl mx-auto">Get a fixed price or request a quote in minutes — no subscription required.</p>
        <Button size="lg" onClick={() => navigate('/book')} className="mt-8 bg-white text-[#102d55] hover:bg-slate-100 font-semibold px-8">Book a service <Icons.ArrowRight className="ml-2 h-4 w-4" /></Button>
      </section>

      <SiteFooter />
    </div>
  );
};

export default Home;
